import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import DatePicker from '@cloudscape-design/components/date-picker';
import FormField from '@cloudscape-design/components/form-field';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { BackfillAggregationType } from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import { backfillAggregationsCallable } from '@/lib/callables';
import { prepareBackfill } from './backfillRange';
import { serverDate, serverNow } from '@etiquetachef/ui-shared/online';

type Props = {
  /** Tipo de agregação que este dashboard exibe — cada tela sabe o seu, então
   *  o seletor de tipo fica implícito (sem campo extra na UI). */
  type: BackfillAggregationType;
};

/**
 * Botão "Recalcular período" para os dashboards alimentados pelo PostHog
 * (Product → `posthog`, Funnel → `funnel`). Abre um modal com intervalo de
 * datas e dispara o callable `backfillAggregations`, que reprocessa os dias
 * direto do PostHog — preenchendo os gaps de "—" deixados quando a Query API
 * falhou no horário do scheduler.
 *
 * Origem: #68 — 2026-06-20 13:00 +0200 (botão "Recalcular período" nos dashboards).
 *
 * Contrato: valida o intervalo com `prepareBackfill` (vazio, invertido ou
 * futuro bloqueia o envio, sem chamar o servidor; "hoje" vem do relógio do
 * navegador, só para essa validação) e chama `backfillAggregations` com
 * `{ type, fromYyyymmdd, toYyyymmdd }`. Sucesso → toast com dias ok/falhos e
 * fecha; erro → mensagem inline no modal. Reprocessa e REGRAVA os docs de
 * agregação do período no servidor.
 *
 * Usado por: `features/dashboard-product/ProductDashboardPage.tsx` (`posthog`),
 * `features/dashboard-funnel/FunnelDashboardPage.tsx` (`funnel`),
 * `features/dashboard-operation/OperationDashboardPage.tsx` (`operation`).
 *
 * Arquitetura: a callable (`apps/functions/src/admin/backfillAggregations.ts`)
 * exige super-admin e é a única via de escrita em `aggregations/**` fora dos
 * schedulers — as Rules negam escrita do cliente.
 */
export function BackfillButton({ type }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Limpa o formulário e fecha o modal. */
  function close(): void {
    setFrom('');
    setTo('');
    setError(null);
    setOpen(false);
  }

  /** Valida o intervalo e dispara a callable de backfill. */
  async function handleSubmit(): Promise<void> {
    const todayIso = serverDate().toISOString().slice(0, 10);
    const prepared = prepareBackfill(from, to, todayIso);
    if (!prepared.ok) {
      setError(t(prepared.errorKey));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await backfillAggregationsCallable({
        type,
        fromYyyymmdd: prepared.fromYyyymmdd,
        toYyyymmdd: prepared.toYyyymmdd,
      });
      toast.success(
        t('backfill.success', {
          success: result.successCount,
          failure: result.failureCount,
        }),
      );
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('backfill.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Button
        iconName="refresh"
        onClick={(): void => {
          setOpen(true);
        }}
      >
        {t('backfill.button')}
      </Button>
      <Modal
        visible={open}
        onDismiss={close}
        header={t('backfill.title')}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={close} disabled={submitting}>
                {t('backfill.cancel')}
              </Button>
              <Button
                variant="primary"
                disabled={!from || !to}
                loading={submitting}
                onClick={(): void => {
                  void handleSubmit();
                }}
              >
                {t('backfill.confirm')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p" color="text-body-secondary">
            {t('backfill.description')}
          </Box>
          <FormField label={t('backfill.from')}>
            <DatePicker
              value={from}
              onChange={({ detail }): void => {
                setFrom(detail.value);
              }}
              isDateEnabled={(date): boolean => date.getTime() < serverNow()}
              format="long-localized"
              openCalendarAriaLabel={(selected): string =>
                t('backfill.openCalendar') + (selected ? `, ${selected}` : '')
              }
            />
          </FormField>
          <FormField label={t('backfill.to')}>
            <DatePicker
              value={to}
              onChange={({ detail }): void => {
                setTo(detail.value);
              }}
              isDateEnabled={(date): boolean => date.getTime() < serverNow()}
              format="long-localized"
              openCalendarAriaLabel={(selected): string =>
                t('backfill.openCalendar') + (selected ? `, ${selected}` : '')
              }
            />
          </FormField>
          {error != null && <Alert type="error">{error}</Alert>}
        </SpaceBetween>
      </Modal>
    </>
  );
}
